/**
 * ExporterIntegrationTestModal — simulates inbound email / webhook calls for
 * the exporter (customer-conformity) AP.
 *
 * Unlike the importer version this does NOT create pending documents; instead
 * it posts to the integration queue endpoints so the simulated request appears
 * in the queue tray on the Aktionen tab.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconMail,
  IconPlugConnected,
  IconSend,
  IconX,
} from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useState } from "react"
import { api } from "../../../api/client"

interface Props {
  apId: string
  emailAddressDisplay?: string
  onClose: () => void
}

type Tab = "email" | "webhook"

export function ExporterIntegrationTestModal({
  apId,
  emailAddressDisplay,
  onClose,
}: Props) {
  const { translate } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>("email")

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        className="relative flex w-[640px] max-w-full flex-col rounded-xl border border-grey4 bg-grey1 shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-grey4 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-white">
              {translate("QMS/INTEGRATION/TEST_TITLE")}
            </h2>
            <p className="mt-0.5 text-xs text-grey5">
              {translate("QMS/INTEGRATION/TEST_SUBTITLE")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-grey5 hover:bg-grey3 hover:text-white"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 gap-1 border-b border-grey4 px-5 py-2">
          <TabButton
            active={activeTab === "email"}
            onClick={() => setActiveTab("email")}
            icon={<IconMail size={14} />}
            label={translate("QMS/INTEGRATION/SIMULATE_EMAIL")}
          />
          <TabButton
            active={activeTab === "webhook"}
            onClick={() => setActiveTab("webhook")}
            icon={<IconPlugConnected size={14} />}
            label={translate("QMS/INTEGRATION/SIMULATE_WEBHOOK")}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "email" ? (
            <EmailForm
              apId={apId}
              emailAddress={emailAddressDisplay ?? ""}
              onClose={onClose}
            />
          ) : (
            <WebhookForm apId={apId} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tab button ─────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active ? "bg-grey3 text-white" : "text-grey5 hover:text-grey6",
      )}
    >
      {icon}
      {label}
    </button>
  )
}

// ── E-Mail form ────────────────────────────────────────────────────────────────

function EmailForm({
  apId,
  emailAddress,
  onClose,
}: {
  apId: string
  emailAddress: string
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const [from, setFrom] = useState("einkauf@kunde-beispiel.de")
  const [subject, setSubject] = useState(
    translate("QMS/EXPORTER/EMAIL_SUBJECT_DEFAULT"),
  )
  const [body, setBody] = useState(translate("QMS/EXPORTER/EMAIL_BODY_DEFAULT"))
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<{
    kind: "ok" | "err"
    msg: string
  } | null>(null)

  const handleSend = async () => {
    setSending(true)
    setStatus(null)
    try {
      await api.integrationQueueEmailSimulate(apId, {
        from_address: from,
        subject,
        body,
      })
      await queryClient.invalidateQueries({
        queryKey: ["integration-queue", apId],
      })
      setStatus({
        kind: "ok",
        msg: translate("QMS/INTEGRATION/REQUEST_QUEUED"),
      })
    } catch (err) {
      setStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : translate("QMS/INTEGRATION/SEND_ERROR"),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-grey5">
        {translate("QMS/INTEGRATION/SIMULATE_EMAIL_DESC")}
      </p>

      <div className="space-y-3">
        <FormField label={translate("QMS/INTEGRATION/FIELD_FROM")}>
          <input
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_TO")}>
          <input
            value={emailAddress || "…"}
            readOnly
            className="w-full cursor-default rounded-md border border-grey4 bg-grey3 px-3 py-1.5 text-sm text-grey5 outline-hidden"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_SUBJECT")}>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_MESSAGE")}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full resize-none rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm leading-relaxed text-white outline-hidden focus:border-yellow1"
          />
        </FormField>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSend}
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-grey1 hover:opacity-90 disabled:opacity-40"
        >
          <IconSend size={14} />
          {sending
            ? translate("QMS/INTEGRATION/SENDING")
            : translate("QMS/INTEGRATION/SEND_EMAIL")}
        </button>
        {status && (
          <span
            className={clsx(
              "text-xs",
              status.kind === "ok" ? "text-success" : "text-red1",
            )}
          >
            {status.msg}
          </span>
        )}
        {status?.kind === "ok" && (
          <button
            onClick={onClose}
            className="ml-auto text-xs text-grey5 hover:text-white"
          >
            {translate("QMS/INTEGRATION/CLOSE")}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Webhook form ───────────────────────────────────────────────────────────────

function WebhookForm({ apId, onClose }: { apId: string; onClose: () => void }) {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const [filename, setFilename] = useState("konformitaetsanfrage-2026.txt")
  const [payload, setPayload] = useState(
    translate("QMS/EXPORTER/WEBHOOK_PAYLOAD_DEFAULT"),
  )
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<{
    kind: "ok" | "err"
    msg: string
  } | null>(null)

  const handleSend = async () => {
    setSending(true)
    setStatus(null)
    try {
      await api.integrationQueueWebhookSimulate(apId, { filename, payload })
      await queryClient.invalidateQueries({
        queryKey: ["integration-queue", apId],
      })
      setStatus({
        kind: "ok",
        msg: translate("QMS/INTEGRATION/REQUEST_QUEUED"),
      })
    } catch (err) {
      setStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : translate("QMS/INTEGRATION/SEND_ERROR"),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-grey5">
        {translate("QMS/INTEGRATION/SIMULATE_WEBHOOK_DESC")}
      </p>

      <div className="space-y-3">
        <FormField label={translate("QMS/INTEGRATION/FIELD_LABEL_FILENAME")}>
          <input
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_PAYLOAD")}>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            className="font-mono w-full resize-none rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm leading-relaxed text-white outline-hidden focus:border-yellow1"
          />
        </FormField>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSend}
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-grey1 hover:opacity-90 disabled:opacity-40"
        >
          <IconPlugConnected size={14} />
          {sending
            ? translate("QMS/INTEGRATION/SENDING")
            : translate("QMS/INTEGRATION/SEND_WEBHOOK")}
        </button>
        {status && (
          <span
            className={clsx(
              "text-xs",
              status.kind === "ok" ? "text-success" : "text-red1",
            )}
          >
            {status.msg}
          </span>
        )}
        {status?.kind === "ok" && (
          <button
            onClick={onClose}
            className="ml-auto text-xs text-grey5 hover:text-white"
          >
            {translate("QMS/INTEGRATION/CLOSE")}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Shared form field ──────────────────────────────────────────────────────────

function FormField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-grey5">
        {label}
      </label>
      {children}
    </div>
  )
}
